import AdsClickOutlined from '@mui/icons-material/AdsClickOutlined';
import ConnectedTv from '@mui/icons-material/ConnectedTv';
import SmartDisplayOutlined from '@mui/icons-material/SmartDisplayOutlined';
import SubtitlesOutlined from '@mui/icons-material/SubtitlesOutlined';
import { PackageFields } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Section, TargetingElementKeys, type TargetingElementKeysType } from '../../context/types.ts';
import { convertApiToSet } from '../../utils/convertApiToSet.ts';
import { useFormatOperations } from '../useFormatOperations.ts';

export const useFormatInitialState = (): Section => {
  const { t } = useTranslation();
  const { getValues } = useFormContext() || { getValues: noop };
  const { onBannerOrNativeClick, onCTVClick, onVideoClick } = useFormatOperations();

  return useMemo(() => {
    const formValue = getValues(`${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.ad_placement.val`);
    const initValues = convertApiToSet(formValue);
    const isSelected = (key: TargetingElementKeysType): boolean => (initValues.size ? initValues.has(key) : false);
    const isDisabled = (key: TargetingElementKeysType): boolean => (initValues.size ? !initValues.has(key) : false);

    return {
      label: t('Format'),
      isLoading: true,
      elements: [
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_BANNER,
            label: t('Banner'),
            icon: <SubtitlesOutlined />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_BANNER),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_BANNER),
            onClick: onBannerOrNativeClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_VIDEO,
            label: t('Video'),
            icon: <SmartDisplayOutlined />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_VIDEO),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_VIDEO),
            onClick: onVideoClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_CTV,
            label: t('CTV'),
            icon: <ConnectedTv />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_CTV),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_CTV),
            onClick: onCTVClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_NATIVE,
            label: t('Native'),
            icon: <AdsClickOutlined />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_NATIVE),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_NATIVE),
            onClick: onBannerOrNativeClick,
          },
        },
      ],
    };
  }, [t, getValues, onCTVClick, onBannerOrNativeClick, onVideoClick]);
};
